/* eslint-disable complexity */
import { dFix } from '@/data/Settings';
import { formatPrice } from '@/utils/formatPrice';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
	OrderSummaryBreakdownDividerSx,
	OrderSummaryBreakdownFrameSx,
	OrderSummaryBreakdownTitleSx,
	OrderSummaryBreakdownTotalSx,
	defaultTypographySx,
} from '@/components/content/Cart/parts/OrderSummaryBreakdown/styles/main';
import { combineSX } from '@/utils/combineSX';
import { IsFullOfferCartAlert } from '@/components/blocks/IsFullOfferCartAlert';
import { ExtendedZoneAlert } from '@/components/blocks/ExtendedZoneAlert';
import { OrderAdjustment } from '@/data/types/Order';
import { useCouponsWithoutRepurchase } from '@/data/Content/RepurchaseCouponState';
import {
	RepurchaseAppliedNotice,
	RepurchaseMiniCartCard,
} from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseFlowCards';
import { OrderCouponRestriction } from '@/components/blocks/OrderCouponRestriction';
import { OrderCouponContextValues } from '@/components/content/Cart/parts/OrderCoupon';
import { ContentContext } from '@/data/context/content';
import { useContext } from 'react';

interface OrderSummaryBreakdownProps {
	total: string | number;
	subtotal: string | number;
	shipping: string | number;
	discount: string | number;
	coupons: string | number;
	totalProducts?: number;
	points?: string | number;
	valeDeCaja?: string | number;
	showTitle?: boolean;
	loading?: boolean;
	confirmation?: boolean;
	isCashPayment?: boolean;
	isMiniCart?: boolean;
	noApplyFreeShippingOffer?: boolean;
	noApplyFreeShippingExtendedZone?: boolean;
	withRepurchaseCartBox?: boolean;
	adjustment?: OrderAdjustment[];
}

export const OrderSummaryBreakdown: React.FC<OrderSummaryBreakdownProps> = ({
	total,
	subtotal,
	shipping,
	discount,
	coupons,
	points,
	totalProducts = 0,
	valeDeCaja,
	showTitle = true,
	loading = false,
	confirmation = false,
	isCashPayment = false,
	isMiniCart = false,
	noApplyFreeShippingOffer = false,
	noApplyFreeShippingExtendedZone = false,
	withRepurchaseCartBox = false,
	adjustment,
}) => {
	const isFreeShipping = dFix(shipping || '9', 0) === 0;
	const {
		order,
		availableCoupons = [],
		inactivePromotionCodes,
	} = useContext(ContentContext) as OrderCouponContextValues;
	const { promotionCode = [] } = order || {};
	const activeAppliedCouponsCount = promotionCode.filter(
		({ code }) => !code || !inactivePromotionCodes?.includes(code)
	).length;

	const couponsDiscount = promotionCode.reduce((sum, promo) => {
		const promotionId = promo.associatedPromotion?.[0]?.promotionId;
		if (!promotionId) return sum;
		const matchingAdjustment = (order?.adjustment ?? []).find((adj) =>
			adj.code?.includes(promotionId)
		);
		return sum + dFix(matchingAdjustment?.amount ?? 0) * -1;
	}, 0);

	const otherCoupons = useCouponsWithoutRepurchase(coupons, adjustment);

	const baseTypographySx = combineSX([
		defaultTypographySx,
		isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
	]);

	return (
		<Box sx={OrderSummaryBreakdownFrameSx}>
			{isMiniCart ? null : (
				<>
					{showTitle ? (
						<Typography component={'h2'} sx={OrderSummaryBreakdownTitleSx}>
							{'RESUMEN DE TU PEDIDO'}
						</Typography>
					) : null}
					{confirmation ? null : (
						<RepurchaseAppliedNotice withCartBox={withRepurchaseCartBox} adjustment={adjustment} />
					)}
					{totalProducts > 0 ? (
						<Stack direction="row" justifyContent="space-between" sx={{ marginBottom: 2 }}>
							<Typography sx={baseTypographySx}>{`${totalProducts} producto${
								totalProducts > 1 ? 's' : ''
							}`}</Typography>
						</Stack>
					) : null}
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Subtotal:'}</Typography>
						<Typography sx={baseTypographySx}>
							{loading ? '...' : formatPrice('es-MX', 'MXN', subtotal)}
						</Typography>
					</Stack>
					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Estás ahorrando:'}</Typography>
						<Typography sx={baseTypographySx} color={'#BF0000'}>
							{formatPrice('es-MX', 'MXN', discount)}
						</Typography>
					</Stack>
					{valeDeCaja ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={baseTypographySx}>{'Monedero:'}</Typography>
							<Typography sx={baseTypographySx} color={'#BF0000'}>
								{'-' + formatPrice('es-MX', 'MXN', valeDeCaja)}
							</Typography>
						</Stack>
					) : null}

					{points ? (
						<Stack direction="row" justifyContent="space-between">
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'Puntos canjeados:'}
							</Typography>
							<Typography sx={combineSX([baseTypographySx, { color: '#004A9B', fontWeight: 600 }])}>
								{'-' + formatPrice('es-MX', 'MXN', points ?? '0')}
							</Typography>
						</Stack>
					) : null}

					<Stack direction="row" justifyContent="space-between">
						<Typography sx={baseTypographySx}>{'Costo de envío:'}</Typography>
						<Typography sx={baseTypographySx} color={isFreeShipping ? '#0E8439' : 'inherit'}>
							{isFreeShipping ? '¡Gratis!' : formatPrice('es-MX', 'MXN', shipping)}
						</Typography>
					</Stack>
					{noApplyFreeShippingOffer ? (
						<IsFullOfferCartAlert />
					) : noApplyFreeShippingExtendedZone ? (
						<ExtendedZoneAlert />
					) : null}

					{otherCoupons > 0 || promotionCode.length > 0 ? (
						<Stack direction="column" gap="4px">
							<Stack direction="row" justifyContent="space-between">
								<Typography
									sx={combineSX([
										baseTypographySx,
										promotionCode.length ? { fontWeight: 700 } : {},
									])}
								>
									{promotionCode.length
										? `Cupones aplicados (${activeAppliedCouponsCount}):`
										: 'Cupones aplicados:'}
								</Typography>
								<Typography sx={baseTypographySx} color={'#BF0000'}>
									{formatPrice('es-MX', 'MXN', couponsDiscount || otherCoupons)}
								</Typography>
							</Stack>
							{promotionCode.map(({ code, associatedPromotion }) => {
								const availableCoupon = availableCoupons.find((coupon) => coupon.code === code);
								return code ? (
									<OrderCouponRestriction
										key={code}
										code={code}
										promotionId={associatedPromotion?.[0]?.promotionId}
										shortDescription={
											availableCoupon?.shortDescription || associatedPromotion?.[0]?.description
										}
									/>
								) : null;
							})}
						</Stack>
					) : null}

					<Divider sx={OrderSummaryBreakdownDividerSx} />
				</>
			)}

			{isMiniCart && !confirmation ? <RepurchaseMiniCartCard adjustment={adjustment} /> : null}
			<Stack direction="row" justifyContent="space-between" alignItems={'center'}>
				<Typography
					sx={combineSX([
						{ fontSize: '15px', fontWeight: 800, textTransform: 'uppercase' },
						isMiniCart
							? {
									fontSize: 'var(--font-size-default)',
									fontWeight: 800,
									textTransform: 'uppercase',
							  }
							: {},
					])}
				>
					{confirmation && isCashPayment ? 'Total a pagar:' : 'Total:'}
				</Typography>
				<Typography
					sx={combineSX([
						OrderSummaryBreakdownTotalSx,
						confirmation && isCashPayment ? { color: '#399300', fontSize: '18px' } : {},
						isMiniCart ? { fontSize: 'var(--font-size-default)' } : {},
					])}
				>
					{formatPrice('es-MX', 'MXN', total)}
				</Typography>
			</Stack>
		</Box>
	);
};
